import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, MapPin, Instagram } from "lucide-react";
import { api, fmtCount, fmtLocation, igUrl } from "@/lib/api";
import { BookDmButton } from "@/components/BookDmDialog";
import { IgEmbed } from "@/components/IgEmbed";
import { Avatar } from "@/components/Avatar";

export default function TechProfile() {
  const { username } = useParams();
  const { data: t, isLoading, isError } = useQuery({ queryKey: ["tech", username], queryFn: () => api.get(`/techs/${username}`).then((r) => r.data) });
  if (isLoading) return <div className="min-h-[60vh]" />;
  if (isError) return <p data-testid="tech-not-found" className="py-32 text-center text-stone-500">This nail tech couldn't be found.</p>;
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-12">
      <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12">
        <aside className="rise rounded-[2rem] border border-[#EAE4DC] bg-white p-8 lg:sticky lg:top-24 lg:col-span-4">
          <div className="ig-ring inline-block"><div className="rounded-full border-4 border-white"><Avatar user={t} className="h-24 w-24" textClass="text-3xl" /></div></div>
          <h1 data-testid="tech-name" className="mt-5 flex items-center gap-2 font-serif text-3xl">{t.name || t.username} <BadgeCheck className="h-5 w-5 text-pink-500" /></h1>
          <a href={igUrl(t.username)} target="_blank" rel="noreferrer" data-testid="tech-ig-link" className="mt-1 inline-flex items-center gap-1 font-mono text-sm text-stone-500 hover:text-pink-600">
            <Instagram className="h-3.5 w-3.5" />@{t.username}
          </a>
          {t.biography && <p data-testid="tech-bio" className="mt-5 whitespace-pre-line text-sm leading-relaxed text-stone-700">{t.biography}</p>}
          {t.followers_count > 0 && <p data-testid="tech-followers" className="mt-3 font-mono text-xs text-stone-500">{fmtCount(t.followers_count)} followers</p>}
          {fmtLocation(t) && <p data-testid="tech-location" className="mt-5 flex items-center gap-2 text-sm text-stone-600"><MapPin className="h-4 w-4" />{fmtLocation(t)}{t.price_tier && <span className="ml-2 font-mono text-xs">{t.price_tier}</span>}</p>}
          <div className="mt-4 flex flex-wrap gap-1.5">
            {t.specialties?.map((s) => <span key={s} className="rounded-full bg-stone-100 px-3 py-1 text-xs">{s}</span>)}
          </div>
          {t.booking_note && <p className="mt-5 rounded-xl bg-[#FAF7F2] p-4 text-sm text-stone-700"><span className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Booking info</span><br />{t.booking_note}</p>}
          <BookDmButton tech={t} className="mt-6 w-full" />
        </aside>
        <section className="lg:col-span-8">
          <p className="mb-5 font-mono text-xs uppercase tracking-[0.2em] text-stone-500">Live from Instagram</p>
          <div className="overflow-hidden rounded-[2rem] border border-[#EAE4DC]"><IgEmbed username={t.username} rows={3} interactive /></div>
        </section>
      </div>
    </div>
  );
}
